import { useCallback, useEffect, useRef, useState } from 'react';

/** Half the spotlight's width/height, so the gradient centres on the cursor. */
const SPOTLIGHT_OFFSET = 120;
/** How long the spotlight lingers after a touch ends. */
const TOUCH_LINGER_MS = 400;

interface Spotlight<T extends HTMLElement> {
  /** Attach to the container. Must be `position: relative`. */
  containerRef: React.RefObject<T>;
  /** Attach to the absolutely-positioned gradient element. */
  spotRef: React.RefObject<HTMLDivElement>;
  /** True while the pointer is over the container (or lingering after a touch). */
  hovered: boolean;
  /** Spread onto the container. */
  handlers: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onMouseMove: (event: React.MouseEvent<T>) => void;
    onTouchStart: (event: React.TouchEvent<T>) => void;
    onTouchEnd: () => void;
  };
}

/**
 * Cursor-following radial spotlight.
 *
 * The gradient is moved with a direct `transform` write rather than React
 * state — repositioning on every mousemove through a re-render is far too
 * expensive for the number of spotlit cards on the page.
 *
 * Coarse pointers skip move tracking (there is no hover to follow) but still
 * get a brief flash on tap so the affordance is not desktop-only.
 */
export function useSpotlight<T extends HTMLElement = HTMLDivElement>(): Spotlight<T> {
  const containerRef = useRef<T>(null);
  const spotRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const lingerTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(lingerTimer.current), []);

  const moveSpot = useCallback((clientX: number, clientY: number): void => {
    const rect = containerRef.current?.getBoundingClientRect();
    const spot = spotRef.current;
    if (!rect || !spot) return;

    const x = clientX - rect.left - SPOTLIGHT_OFFSET;
    const y = clientY - rect.top - SPOTLIGHT_OFFSET;
    spot.style.transform = `translate(${x}px, ${y}px)`;
  }, []);

  const onMouseMove = useCallback(
    (event: React.MouseEvent<T>): void => {
      if (window.matchMedia('(pointer: coarse)').matches) return;
      moveSpot(event.clientX, event.clientY);
    },
    [moveSpot],
  );

  const onTouchStart = useCallback(
    (event: React.TouchEvent<T>): void => {
      const touch = event.touches[0];
      if (touch) moveSpot(touch.clientX, touch.clientY);
      window.clearTimeout(lingerTimer.current);
      setHovered(true);
    },
    [moveSpot],
  );

  const onTouchEnd = useCallback((): void => {
    lingerTimer.current = window.setTimeout(() => setHovered(false), TOUCH_LINGER_MS);
  }, []);

  const onMouseEnter = useCallback((): void => setHovered(true), []);
  const onMouseLeave = useCallback((): void => setHovered(false), []);

  return {
    containerRef,
    spotRef,
    hovered,
    handlers: { onMouseEnter, onMouseLeave, onMouseMove, onTouchStart, onTouchEnd },
  };
}
